import * as React from 'react';
import { IFacetDTO, ITermDTO } from '../../DTO/search';
import { IContentProps } from '../../models/content-props';
import MediaQuery from 'react-responsive';

interface IProps {
    facets: IFacetDTO[];
    contentProps: IContentProps;
    selectedTermIds: number[];
    onTermClick: (id: number) => void;
    onResetClick: () => void;
}

interface IFacetState {
    allFacetsOpen: boolean;
}

export class Facets extends React.Component<IProps, any> {
    constructor(props) {
        super(props);
        this.state = {
            allFacetsOpen: false
        }
    }

    renderFacet = (value, index) => {
        return <Facet
            facet={value}
            key={index}
            selectedTermIds={this.props.selectedTermIds}
            onTermClick={this.props.onTermClick} 
            showAll={this.props.facets.length<=2} />
    }

    toggleFacets = () => {
        this.setState({ allFacetsOpen: !this.state.allFacetsOpen });
    }

    render() {
        if (!this.props.facets || this.props.facets.length <= 0) {
            return null;
        }

        return (
            <>
                <MediaQuery maxWidth={760}>
                    <button type="button" className="directory-filter-drawer un-button" aria-expanded={this.state.allFacetsOpen} onClick={this.toggleFacets}>
                        <span className="fas fa-sliders-h" aria-hidden={true}></span>
                        <span>Filters</span>
                        <span className="directory-filter-drawer-indicator" aria-hidden={true}></span>
                    </button>
                </MediaQuery>
                <div className="directory-sidebar-group card-padding-mini">
                    <h2 className="visually-hidden">Search Filters</h2>
                    {this.props.facets.map(this.renderFacet)}
                </div>
                <div className="directory-sidebar-group card-padding-mini directory-filter-reset">
                    <button type="button" onClick={this.props.onResetClick} className="un-button hyperlink">{this.props.contentProps.resetButtonText}</button>
                </div>
            </>
        )
    }
}

interface IFacetProps {
    facet: IFacetDTO;
    selectedTermIds: number[];
    showAll: boolean;
    onTermClick: (id: number) => void;
}

interface IState {
    showAll: boolean;
}

export class Facet extends React.Component<IFacetProps, IState> {
    constructor(props) {
        super(props);

        this.state = {
            showAll: this.props.showAll
        }
    }

    renderTerms = (value, index) => {
        return <Term
            key={index}
            selected={this.props.selectedTermIds.some(id => id === value.id)}
            term={value}
            onTermClick={this.props.onTermClick} />
    }

    render() {
        const { facet } = this.props;

        if (!facet.terms || facet.terms.length === 0) {
            return null;
        }

        let terms = facet.terms.filter(x => x.term && x.term.length > 0);

        if (terms.length <= 0) {
            return null;
        }

        if (facet.fieldName === 'Facets.OverallRating' || facet.fieldName === 'Facets.Network')
            terms = this.getSortedTerms(terms);


        let splicedTerms = !this.state.showAll && terms.length > facet.showMoreAmount
            ? terms.splice(0, facet.showMoreAmount)
            : terms;

        return (
            <>
                {
                    facet.showMoreAmount > 0 ?
                        <fieldset className="directory-filter-group">
                            <legend><h3 className="h4-style">{
                                // displayName doesn't appear to return anything, I don't know if this is a bug or config issue
                                facet.displayName ?
                                    facet.displayName
                                    : 'Filters'
                            }</h3></legend>
                            <ul className="no-bullets directory-filter-list">
                                {splicedTerms.map(this.renderTerms)}
                            </ul>
                            {terms.length > facet.showMoreAmount && !this.state.showAll && <button type="button" className="un-button hyperlink" onClick={() => this.setShowAll(true)}>Show More</button>}
                            {terms.length > facet.showMoreAmount && this.state.showAll && <button type="button" className="un-button hyperlink" onClick={() => this.setShowAll(false)}>Show Fewer</button>}
                        </fieldset>
                        :
                        <div className="panel-container">
                            <button type="button" className="un-button directory-filter-dropdown-button h5-style"
                                onClick={() => this.toggleFilters()} aria-expanded={this.state.showAll}>
                                {facet.displayName}
                                <span className="directory-filter-dropdown-indicator" aria-hidden={true}></span>
                            </button>
                            <div aria-hidden={!this.state.showAll} className="directory-filter-panel">
                                <ul className="no-bullets directory-filter-list">
                                    {splicedTerms.map(this.renderTerms)}
                                </ul>
                            </div>
                        </div>
                }
            </>
        )
    }

    setShowAll = (value: boolean) => {
        this.setState({ showAll: value });
    }

    toggleFilters = () => {
        this.setState({ showAll: !this.state.showAll });
    }

    getSortedTerms = (terms: ITermDTO[]) => {

        let result = terms.sort(function (a, b) {
            let valA;
            let valB;
            // Sort network numbers by the number they end in
            if (a.term && b.term && a.term.startsWith("Network ") && b.term.startsWith("Network ")) {
                try {
                    valA = parseInt(a.term.split(' ')[1]);
                    valB = parseInt(b.term.split(' ')[1]);
                }
                catch (ex) {
                    console.log(ex);
                }
            }
            // Sort Levels that end with + before the matching levels that don't end in +
            else if ((a.term.endsWith('+') && a.term.substring(0, a.term.length - 1) == b.term)) {
                valA = parseInt(a.term.split(' ')[1].replace('+', '')) - 1;
                valB = parseInt(b.term.split(' ')[1].replace('+', ''));
            }
            else if (b.term.endsWith('+') && b.term.substring(0, b.term.length - 1) == a.term) {
                valA = parseInt(a.term.split(' ')[1].replace('+', ''));
                valB = parseInt(b.term.split(' ')[1].replace('+', '')) - 1;
            }
            else {
                valA = a.term.toUpperCase();
                valB = b.term.toUpperCase();
            }
            return (valA < valB) ? -1 : (valA > valB) ? 1 : 0;
        });

        // They want to force 'inability to rate' to be the last item
        let inabilityToRate = result.find(x => x.term.toLowerCase() === "inability to rate");
        if (inabilityToRate) {
            let indexOfInabilityToRate = result.indexOf(inabilityToRate);
            if (indexOfInabilityToRate > -1) {
                result.splice(indexOfInabilityToRate, 1);
                result.push(inabilityToRate);
            }
        }

        return result;
    }
}

interface ITermProps {
    term: ITermDTO;
    selected: boolean;
    onTermClick: (id: number) => void;
}

export class Term extends React.Component<ITermProps, any> {
    render() {
        const { term, onTermClick, selected } = this.props;
        let id = `${term.fieldName.replace(/[ ./\\]/g, "-")}.${term.term.replace(/[ ./\\]/g, "-")}`;
        return (
            <li className="directory-filter-list-item">
                <input type="checkbox"
                    onChange={() => onTermClick(term.id)}
                    id={id}
                    name={id}
                    checked={selected}
                    value={term.term} />
                <label htmlFor={id} className="directory-filter-checkbox">{term.displayName} <span className="regular">({term.hits})</span></label>
            </li>
        )
    }
}